import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { site } from "@/config/site";

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-[#06111F]/85 backdrop-blur-md border-b border-white/10"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 h-[72px] flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2" data-testid="nav-home-link">
                    <Logo size="md" />
                </Link>

                <nav className="hidden lg:flex items-center gap-7">
                    {site.nav.map((item) => (
                        <NavLink
                            key={item.href}
                            to={item.href}
                            data-testid={`nav-${item.href.replace("/", "") || "home"}`}
                            className={({ isActive }) =>
                                `text-[15px] font-medium link-underline transition-colors ${
                                    isActive
                                        ? "text-[#14B8A6]"
                                        : "text-white/85 hover:text-white"
                                }`
                            }
                            end={item.href === "/"}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <a
                        href={`tel:${site.phoneLink}`}
                        className="text-sm font-medium text-white/85 hover:text-[#14B8A6] flex items-center gap-1.5"
                        data-testid="nav-phone"
                    >
                        <Phone className="w-4 h-4" />
                        {site.phoneDisplay}
                    </a>
                    <Link
                        to="/kontakt"
                        data-testid="nav-cta-consultation"
                        className="inline-flex items-center justify-center px-5 py-2.5 rounded-[12px] bg-[#14B8A6] hover:bg-[#0EA5A5] text-white font-semibold text-sm transition-all duration-200 hover:-translate-y-0.5 shadow-[0_8px_20px_-6px_rgba(20,184,166,0.5)]"
                    >
                        Umów konsultację
                    </Link>
                </div>

                <button
                    aria-label={open ? "Zamknij menu" : "Otwórz menu"}
                    onClick={() => setOpen((v) => !v)}
                    data-testid="nav-mobile-toggle"
                    className="lg:hidden text-white p-2"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden bg-[#06111F] border-t border-white/10" data-testid="nav-mobile-panel">
                    <nav className="px-5 py-6 flex flex-col gap-4">
                        {site.nav.map((item) => (
                            <Link
                                key={item.href}
                                to={item.href}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-mobile-${item.href.replace("/", "") || "home"}`}
                                className="text-white text-base font-medium py-2"
                            >
                                {item.label}
                            </Link>
                        ))}
                        <a
                            href={`tel:${site.phoneLink}`}
                            className="text-white/85 text-base font-medium pt-2 border-t border-white/10 flex items-center gap-2"
                            data-testid="nav-mobile-phone"
                        >
                            <Phone className="w-4 h-4 text-[#14B8A6]" />
                            {site.phoneDisplay}
                        </a>
                        <Link
                            to="/kontakt"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-cta"
                            className="mt-2 px-5 py-3 rounded-[12px] bg-[#14B8A6] text-white font-semibold text-center"
                        >
                            Umów konsultację
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
};
